import {
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  getCollectionPointsWithRegionID_getCollectionPointsWithRegionID_entries,
  getCollectionPointsWithRegionID_getCollectionPointsWithRegionID_entries_collection_groups,
} from "../../../../api/graphql/queries/types/getCollectionPointsWithRegionID"
import { SmoothCell } from "../../../partials/table/smooth-cell"
import { useCollectionGroupWizardContext } from "../context/collection-group-wizard-context"
import { CollectionGroupDetailPopover } from "./collection-group-detail-popover"

type CollectionPoint = getCollectionPointsWithRegionID_getCollectionPointsWithRegionID_entries
export type CollectionPointCollectionGroup =
  getCollectionPointsWithRegionID_getCollectionPointsWithRegionID_entries_collection_groups

export const CollectionGroupCollectionPointsTableDragNDrop: React.FC = () => {
  const { t } = useTranslation()
  const { collectionPointsForTable, cpFilter, orderOn, mapUpdate,
    setMapUpdate, setCpFilter, cpForTableLoading, loading, formValues, 
    setFormValues, selectedTableEntries,
    setSortedCollectionPoints
  } =
    useCollectionGroupWizardContext()
  const [anchorEl, setAnchorEl] = useState<Element | undefined>()
  const [collectionGroups, setCollectionGroups] = useState<CollectionPointCollectionGroup[]>([])
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const entries = useMemo(() => selectedTableEntries, [selectedTableEntries])
  const [localSortedCollectionPoints, setLocalSortedCollectionPoints] = useState(entries)
  useEffect(() => {

    const orderedEntries = [
      ...(formValues?.orderedGroupPoints || [])
        .map((p) => entries?.find((e) => +e.id === +(p.id || -1)))
        .filter((e): e is NonNullable<typeof e> => e != null),
    
      ...(entries?.filter(
        (e) =>
          !(formValues?.orderedGroupPoints || [])
            .map((o) => +(o?.id || -1))
            .includes(+e.id)
      ) || []),
    ];

    
    setLocalSortedCollectionPoints(orderedEntries);
    if(setSortedCollectionPoints){
      setSortedCollectionPoints(orderedEntries.map((cp) => +(cp.id)));
    }
    if(setMapUpdate){
      setMapUpdate(!mapUpdate);
    }
  }, [entries]);

  useEffect(() => {
    if(setSortedCollectionPoints){
      setSortedCollectionPoints(localSortedCollectionPoints.map((cp) => +(cp.id)));
    }

    if(setMapUpdate){
      setMapUpdate(!mapUpdate);
    }
  }, [localSortedCollectionPoints]);
  
  const handleDragStart = (id: string) => {
    if(orderOn){
      setDraggingId(id);
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
    if(orderOn){
      e.preventDefault();
    }
  };
  
  const handleDrop = (targetId: string) => {
    if (!orderOn || !draggingId) return;
  
    const draggedIndex = localSortedCollectionPoints.findIndex((cp) => cp.id === draggingId);
    const targetIndex = localSortedCollectionPoints.findIndex((cp) => cp.id === targetId);
  
    if (draggedIndex === -1 || targetIndex === -1) return;
  
    const updated = [...localSortedCollectionPoints];
    const [removed] = updated.splice(draggedIndex, 1);
    updated.splice(targetIndex, 0, removed);
  
    setLocalSortedCollectionPoints(updated);
    setDraggingId(null);
  };
  
  const getMaxHeight = useCallback(() => {
    const wizardIndicatorHeight = 100
    const headerHeight = 73
    const filterBarHeight = 72
    const tableHeaderRow = 75
    const buttonWizardRow = 68

    const height =
      window.innerHeight - wizardIndicatorHeight - headerHeight - filterBarHeight - tableHeaderRow - buttonWizardRow
    return height > 0 ? height : 0
  }, [])

  const [maxHeight, setMaxHeight] = useState(getMaxHeight())

  useEffect(() => {
    const handleResize = () => {
      setMaxHeight(getMaxHeight())
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [getMaxHeight])



  const getCollectionPointAddress = (
    collectionPoint: getCollectionPointsWithRegionID_getCollectionPointsWithRegionID_entries,
  ) => {
    const addressParts = []
    if (collectionPoint.street) {
      addressParts.push(collectionPoint.street)
    }
    if (collectionPoint.cadastral_nr) {
      addressParts.push(collectionPoint.cadastral_nr)
    }
    if (collectionPoint.cadastral_name) {
      addressParts.push(collectionPoint.cadastral_name)
    }

    return addressParts.length > 0 ? addressParts.join(", ") : "-"
  }

  const getGroupCount = useCallback(
    (groups: CollectionPointCollectionGroup[] | null) => {
      if (!groups) return 0
      const selectedMaterialsIds = formValues?.materialIds ?? []
      return groups.filter((group) => {
        const materialIds = (group.materials ?? []).map((m) => m.id)
        return (
          selectedMaterialsIds.length === materialIds.length &&
          materialIds.every((id) => selectedMaterialsIds.includes(Number(id)))
        )
      }).length
    },
    [formValues],
  )

  const isSelected = (cpId: string) => {
    return formValues?.collectionPointIds?.includes(Number(cpId)) ?? false
  }
  const handleCheckboxChange = useCallback(
    (checked: boolean, collectionPoint: CollectionPoint) => {
      if (checked) {
        setFormValues({
          ...formValues,
          collectionPointIds: [...(formValues?.collectionPointIds ?? []), Number(collectionPoint.id)],
        })
      } else {
        setFormValues({
          ...formValues,
          collectionPointIds: (formValues?.collectionPointIds ?? []).filter((id) => id !== Number(collectionPoint.id)),
        })
      }
    },
    [formValues, setFormValues],
  )
  const handleGroupInfoClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, collectionPoint: CollectionPoint) => {
      const count = getGroupCount(collectionPoint.collection_groups)
      if (count === 0) return
      const selectedMaterialsIds = formValues?.materialIds ?? []

      const filtered = (collectionPoint.collection_groups ?? [])
        .filter((group) => {
          const groupMaterials = group.materials ?? []
          return (
            groupMaterials.every((m) => selectedMaterialsIds.includes(Number(m.id))) &&
            groupMaterials.length === selectedMaterialsIds.length
          )
        })
        .sort((a, b) => a.label.localeCompare(b.label))

      setCollectionGroups(filtered)
      setAnchorEl(e.currentTarget)
    },
    [formValues, getGroupCount],
  )

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      {anchorEl && (
        <CollectionGroupDetailPopover
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          collectionGroups={collectionGroups}
        />
      )}
      <TableContainer
        id="collection-points-for-collection-group-table"
        sx={{ minHeight: 150, maxHeight: maxHeight, overscrollBehavior: "contain" }}
      >
        <Table aria-label="collection points table" stickyHeader key={+(mapUpdate || 0)+"table"}>
          <TableHead>
            <TableRow id="header-table-row">
              <SmoothCell align="center">
              </SmoothCell>
              <SmoothCell align="center">
                <Typography fontWeight={700}>{t("collection_groups.mutate_page.address").toUpperCase()}</Typography>
              </SmoothCell>
              <SmoothCell align="center">
                <Typography fontWeight={700}>{t("collection_groups.mutate_page.description").toUpperCase()}</Typography>
              </SmoothCell>
              <SmoothCell align="center">
                <Typography fontWeight={700}>{t("collection_groups.mutate_page.town").toUpperCase()}</Typography>
              </SmoothCell>
              <SmoothCell align="center">
                <TableSortLabel
                  direction={cpFilter.sortByGroupCountAsc ? "asc" : "desc"}
                  onClick={() => setCpFilter({ ...cpFilter, sortByGroupCountAsc: !cpFilter.sortByGroupCountAsc })}
                >
                  <Typography fontWeight={700}>{t("collection_groups.mutate_page.groups").toUpperCase()}</Typography>
                </TableSortLabel>
              </SmoothCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cpForTableLoading && !loading && (
              <TableRow>
                <SmoothCell colSpan={5} align="center">
                  <Box display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                </SmoothCell>
              </TableRow>
            )}
            {localSortedCollectionPoints.length === 0 && !cpForTableLoading && (
              <TableRow>
                <SmoothCell colSpan={5} align="center">
                  <Box display="flex" justifyContent="center">
                    <Typography>{t("collection_group_overview.table.no_data")}</Typography>
                  </Box>
                </SmoothCell>
              </TableRow>
            )}
            {!cpForTableLoading &&
              localSortedCollectionPoints.length > 0 &&
              localSortedCollectionPoints.map((collectionPoint, index) => (
                <TableRow
                  key={collectionPoint.id}
                  draggable
                  onDragStart={() => handleDragStart(collectionPoint.id)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(collectionPoint.id)}
                  sx={{
                    cursor: orderOn ? "move" : "&:hover",
                    backgroundColor:
                      draggingId === collectionPoint.id ? "action.hover" : "inherit",
                  }}
                >
                  <SmoothCell align="center">
                    {orderOn && <Typography>{index + 1}</Typography>}
                    {!orderOn &&  <Checkbox
                      checked={isSelected(collectionPoint.id)}
                      onChange={(ev) => handleCheckboxChange(ev.target.checked, collectionPoint)}
                    />}
                  </SmoothCell>
                  <SmoothCell align="center">
                    <Typography>{getCollectionPointAddress(collectionPoint)}</Typography>
                  </SmoothCell>
                  <SmoothCell align="center">
                    <Typography>{collectionPoint.description}</Typography>
                  </SmoothCell>
                  <SmoothCell align="center">
                    <Typography>{collectionPoint.town?.name ?? ""}</Typography>
                  </SmoothCell>
                  <SmoothCell align="center">
                    <Typography onClick={(e) => handleGroupInfoClick(e, collectionPoint)}>
                      ({getGroupCount(collectionPoint.collection_groups)})
                    </Typography>
                  </SmoothCell>
                </TableRow>
              ))}
          </TableBody>

        </Table>
      </TableContainer>

      <TablePagination
      component="div"
      rowsPerPageOptions={[]}
      count={entries.length}
      rowsPerPage={entries.length}
      page={0}
      onPageChange={() => {}}
      onRowsPerPageChange={() => {}}
    />
    </Paper>
  )
}
